/**
 * Canva Template Generator
 *
 * Converts parsed Canva HTML + user mappings into a storable email template
 * with placeholders for dynamic content.
 */

import type { TextBlock, Button } from './canva-html-parser';
import type { SocialMediaLinks } from './canva-social-detector';
import { replaceSocialLinksInHtml } from './canva-social-detector';

export type TextBlockMappingType = 'none' | 'main_message' | 'header_text' | 'unsubscribe';

export type ButtonMappingType = 'keep_original' | 'review' | 'coupon' | 'loyalty' | 'custom';

export interface TextBlockMapping {
  blockId: string;
  mappingType: TextBlockMappingType;
}

export interface ButtonMapping {
  buttonId: string;
  mappingType: ButtonMappingType;
  // For review/coupon/loyalty - the ID of the selected form/offer/program
  targetId?: string;
  // For custom - the custom URL
  customUrl?: string;
}

export interface CanvaTemplateMappings {
  textBlocks: TextBlockMapping[];
  buttons: ButtonMapping[];
}

export interface GeneratedTemplate {
  html: string;
  mainMessage: string;
  headerText: string;
  canvaBlocks: CanvaBlock[];
  socialLinks?: SocialMediaLinks;
}

export interface CanvaBlock {
  id: string;
  type: 'editable_text' | 'cta_button' | 'unsubscribe';
  originalContent?: string;
  label?: string;
  buttonType?: ButtonMappingType;
  targetId?: string;
  customUrl?: string;
}

/** Manual social text mappings: platform -> textBlockId */
export type SocialTextMappings = Record<keyof SocialMediaLinks, string | null>;

/**
 * Generate a storable template from parsed HTML and user mappings
 *
 * NOTE: This keeps original text in the HTML. Placeholders are only used
 * when sending emails via processTemplateForSending().
 */
export function generateCanvaTemplate(
  html: string,
  textBlocks: TextBlock[],
  buttons: Button[],
  mappings: CanvaTemplateMappings,
  socialLinks?: SocialMediaLinks,
  socialTextMappings?: SocialTextMappings
): GeneratedTemplate {
  let processedHtml = html;
  const canvaBlocks: CanvaBlock[] = [];
  let mainMessage = '';
  let headerText = '';

  // Process text block mappings
  for (const mapping of mappings.textBlocks) {
    const block = textBlocks.find(b => b.id === mapping.blockId);
    if (!block) continue;

    switch (mapping.mappingType) {
      case 'main_message':
        // Store original content as main message (keep original in HTML)
        mainMessage = block.content;
        // DON'T replace in HTML - keep original text
        // The replacement happens at send time via processTemplateForSending()
        canvaBlocks.push({
          id: block.id,
          type: 'editable_text',
          originalContent: block.content,
          label: 'Main Message'
        });
        break;

      case 'header_text':
        // Store original content as header text (keep original in HTML)
        headerText = block.content;
        // DON'T replace in HTML - keep original text
        // The replacement happens at send time via processTemplateForSending()
        canvaBlocks.push({
          id: block.id,
          type: 'editable_text',
          originalContent: block.content,
          label: 'Header Text'
        });
        break;

      case 'unsubscribe':
        // Wrap with unsubscribe URL placeholder
        processedHtml = wrapTextWithLink(
          processedHtml,
          block.content,
          '{{UNSUBSCRIBE_URL}}'
        );
        canvaBlocks.push({
          id: block.id,
          type: 'unsubscribe',
          originalContent: block.content,
          label: 'Unsubscribe Link'
        });
        break;

      case 'none':
      default:
        // Keep as-is
        break;
    }
  }

  // Process button mappings
  for (const mapping of mappings.buttons) {
    const button = buttons.find(b => b.id === mapping.buttonId);
    if (!button) continue;

    if (mapping.mappingType === 'keep_original') {
      // Keep original href
      continue;
    }

    // Create placeholder based on button type
    const placeholder = `{{BUTTON:${button.id}:${mapping.mappingType}:${mapping.targetId || mapping.customUrl || ''}}}`;

    // Replace href in HTML
    processedHtml = replaceButtonHref(processedHtml, button.originalHref, placeholder);

    canvaBlocks.push({
      id: button.id,
      type: 'cta_button',
      originalContent: button.text,
      label: button.text,
      buttonType: mapping.mappingType,
      targetId: mapping.targetId,
      customUrl: mapping.customUrl
    });
  }

  // Process manual social text mappings - wrap selected text blocks with anchor tags
  if (socialTextMappings && socialLinks) {
    for (const [platform, blockId] of Object.entries(socialTextMappings) as [keyof SocialMediaLinks, string | null][]) {
      if (blockId && socialLinks[platform]) {
        const block = textBlocks.find(b => b.id === blockId);
        if (block) {
          processedHtml = wrapTextWithLink(processedHtml, block.content, socialLinks[platform]!);
        }
      }
    }
  }

  // Replace auto-detected social links in HTML with business profile URLs if provided
  if (socialLinks) {
    processedHtml = replaceSocialLinksInHtml(processedHtml, socialLinks);
  }

  return {
    html: processedHtml,
    mainMessage,
    headerText,
    canvaBlocks,
    socialLinks
  };
}

/**
 * Replace text content in HTML while preserving surrounding tags
 */
function replaceTextContent(html: string, originalText: string, replacement: string): string {
  // Clean text for matching (handle whitespace variations)
  const cleanOriginal = originalText.trim();

  // Try to find and replace in td elements with dir="ltr"
  // This regex captures the opening tag, text content, and closing tag
  const patterns = [
    // Exact match in td[dir="ltr"]
    `(<td[^>]*dir=["']ltr["'][^>]*>)([^<]*?)(</td>)`,
    // Match with potential <br> at the end
    `(<td[^>]*dir=["']ltr["'][^>]*>)([^<]*?)(\\s*<br\\s*/?>\\s*</td>)`
  ];

  for (const patternStr of patterns) {
    const pattern = new RegExp(patternStr, 'gi');
    const newHtml = html.replace(pattern, (match, openTag, textContent, closeTag) => {
      const cleanContent = textContent.trim();
      if (cleanContent === cleanOriginal || normalizeText(cleanContent) === normalizeText(cleanOriginal)) {
        // Preserve <br> if it was there
        const hasBr = closeTag.includes('<br');
        return `${openTag}${replacement}${hasBr ? '<br>' : ''}${closeTag.replace(/<br\s*\/?>/gi, '')}`;
      }
      return match;
    });

    if (newHtml !== html) {
      return newHtml;
    }
  }

  return html;
}

/**
 * Wrap text content with a link
 */
function wrapTextWithLink(html: string, originalText: string, href: string): string {
  const cleanOriginal = originalText.trim();

  // Find the text in td elements and wrap with anchor
  const pattern = /(<td[^>]*dir=["']ltr["'][^>]*>)([^<]*?)(<\/td>)/gi;

  return html.replace(pattern, (match, openTag, textContent, closeTag) => {
    const cleanContent = textContent.trim();
    if (cleanContent === cleanOriginal || normalizeText(cleanContent) === normalizeText(cleanOriginal)) {
      return `${openTag}<a href="${href}" style="color:inherit;text-decoration:underline">${textContent}</a>${closeTag}`;
    }
    return match;
  });
}

/**
 * Replace button href in HTML
 */
function replaceButtonHref(html: string, originalHref: string, newHref: string): string {
  // Handle both # and actual URLs
  const escapedHref = originalHref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`href=["']${escapedHref}["']`, 'gi');
  return html.replace(pattern, `href="${newHref}"`);
}

/**
 * Normalize text for comparison (handle whitespace, line breaks, etc.)
 */
function normalizeText(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\u00a0/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Resolve button placeholder to actual URL
 *
 * Used when sending emails to replace {{BUTTON:...}} with real URLs.
 */
export function resolveButtonPlaceholder(placeholder: string, baseUrl: string = 'https://taginn.app'): string {
  // Parse placeholder: {{BUTTON:button_id:type:targetId}}
  const match = placeholder.match(/\{\{BUTTON:([^:]+):([^:]+):([^}]*)\}\}/);
  if (!match) return '#';

  const [, _buttonId, type, targetOrUrl] = match;

  switch (type) {
    case 'review':
      return targetOrUrl ? `${baseUrl}/review/${targetOrUrl}` : '#';
    case 'coupon':
      return targetOrUrl ? `${baseUrl}/coupon-offer/${targetOrUrl}` : '#';
    case 'loyalty':
      return targetOrUrl ? `${baseUrl}/get-pass?template=${targetOrUrl}` : '#';
    case 'custom':
      return targetOrUrl || '#';
    default:
      return '#';
  }
}

/**
 * Process template HTML for sending
 *
 * Replaces original main message text, header text, and resolves placeholders.
 */
export function processTemplateForSending(
  html: string,
  options: {
    mainMessage?: string;
    originalMainMessage?: string; // The original text to find and replace
    headerText?: string;
    originalHeaderText?: string; // The original header text to find and replace
    unsubscribeUrl?: string;
    subject?: string;
    currentYear?: string;
  }
): string {
  let processed = html;

  // Replace header text - find original text and replace with edited version
  if (options.headerText !== undefined && options.originalHeaderText) {
    processed = replaceTextContent(processed, options.originalHeaderText, options.headerText);
  }

  // Replace main message - find original text and replace with edited version
  if (options.mainMessage !== undefined && options.originalMainMessage) {
    // Replace the original main message text with the edited version
    processed = replaceTextContent(processed, options.originalMainMessage, options.mainMessage);
  }

  // Also handle legacy {{MAIN_MESSAGE}} placeholder for backwards compatibility
  if (options.mainMessage !== undefined) {
    processed = processed.replace(/\{\{MAIN_MESSAGE\}\}/g, options.mainMessage);
  }

  // Replace unsubscribe URL
  if (options.unsubscribeUrl) {
    processed = processed.replace(/\{\{UNSUBSCRIBE_URL\}\}/g, options.unsubscribeUrl);
  }

  // Replace subject
  if (options.subject) {
    processed = processed.replace(/\{\{SUBJECT\}\}/g, options.subject);
  }

  // Replace year
  if (options.currentYear) {
    processed = processed.replace(/\{\{CURRENT_YEAR\}\}/g, options.currentYear);
  }

  // Resolve button placeholders
  const buttonPattern = /\{\{BUTTON:[^}]+\}\}/g;
  processed = processed.replace(buttonPattern, (match) => resolveButtonPlaceholder(match));

  return processed;
}
